import type {
  CcsConfiguration,
  GenerationImpactScenarioInput,
  GenerationTechnology,
} from '../../types/generationImpact';

export interface PlanningScenarioArgs {
  siteId: string;
  name?: string;
  facilityLoadMW: number;
  criticalLoadMW?: number;
  technology: GenerationTechnology;
  blockCount: number;
  grossMWPerBlock: number;
  ccsEnabled?: boolean;
  targetPhaseId?: string;
  layoutAnchor?: { lat: number; lng: number };
  /**
   * Grid relationship declared in Project Intent for the owning phase.
   * `GridConnectionMode` and this field are the same union, so this is a
   * straight carry-over of a decision the user already made — omit it and the
   * scenario silently opens "islanded" no matter what the project says.
   */
  gridConnection?: GenerationImpactScenarioInput['gridConnection'];
  /**
   * Generation contingency groups the fleet must survive losing. Omit to keep
   * the N-1 screening default.
   */
  contingencyOutages?: number;
}

const ARCHETYPES: Record<GenerationTechnology, {
  heatRate: number;
  ambientFactor: number;
  degradationFactor: number;
  auxiliaryFraction: number;
  forcedOutageRate: number;
  withdrawalGalPerMWh: number;
  consumptionGalPerMWh: number;
  footprintAcresPerMW: number;
  citation: string;
}> = {
  'reciprocating-engine': {
    heatRate: 9_000, ambientFactor: 0.95, degradationFactor: 0.98, auxiliaryFraction: 0.03,
    forcedOutageRate: 0.08, withdrawalGalPerMWh: 20, consumptionGalPerMWh: 15, footprintAcresPerMW: 0.4,
    citation: 'Platform planning archetype: EIA natural-gas heat-rate basis; gas-power DAM gas-006; vendor confirmation required.',
  },
  'simple-cycle-gas-turbine': {
    heatRate: 10_500, ambientFactor: 0.90, degradationFactor: 0.98, auxiliaryFraction: 0.03,
    forcedOutageRate: 0.15, withdrawalGalPerMWh: 5, consumptionGalPerMWh: 3, footprintAcresPerMW: 0.4,
    citation: 'Platform GAS_SC_BENCHMARKS (EIA AEO peaker class) and gas-power DAM gas-006; vendor correction curve required.',
  },
  'combined-cycle-block': {
    heatRate: 7_000, ambientFactor: 0.90, degradationFactor: 0.98, auxiliaryFraction: 0.04,
    forcedOutageRate: 0.08, withdrawalGalPerMWh: 500, consumptionGalPerMWh: 400, footprintAcresPerMW: 0.4,
    citation: 'Platform GAS_CC_BENCHMARKS (NREL ATB/EIA AEO) and EPRI thermoelectric water planning ranges; vendor heat balance required.',
  },
  'grid-import': {
    heatRate: 0, ambientFactor: 0.97, degradationFactor: 1, auxiliaryFraction: 0,
    forcedOutageRate: 0.03, withdrawalGalPerMWh: 0, consumptionGalPerMWh: 0, footprintAcresPerMW: 0,
    citation: 'Project phase-feasibility default: 97% firm grid capacity credit. Upstream generation impacts remain outside the site boundary.',
  },
  'battery-storage': {
    heatRate: 0, ambientFactor: 0.95, degradationFactor: 1, auxiliaryFraction: 0,
    forcedOutageRate: 0.05, withdrawalGalPerMWh: 0, consumptionGalPerMWh: 0, footprintAcresPerMW: 0,
    citation: 'Project phase-feasibility default: 95% availability within the rated discharge window. Energy duration and charging source remain required inputs.',
  },
  'solar-bess': {
    heatRate: 0, ambientFactor: 0.30, degradationFactor: 1, auxiliaryFraction: 0,
    forcedOutageRate: 0, withdrawalGalPerMWh: 0, consumptionGalPerMWh: 0, footprintAcresPerMW: 5,
    citation: 'Project phase-feasibility defaults: 30% firm capacity credit for flat 24/7 load and 5 acres/MW nameplate; site-specific ELCC and layout required.',
  },
  'wind-bess': {
    heatRate: 0, ambientFactor: 0.30, degradationFactor: 1, auxiliaryFraction: 0,
    forcedOutageRate: 0, withdrawalGalPerMWh: 0, consumptionGalPerMWh: 0, footprintAcresPerMW: 0.75,
    citation: 'Project phase-feasibility defaults: 30% firm capacity credit for flat 24/7 load and 0.75 permanent acres/MW nameplate; site-specific ELCC required.',
  },
  'nuclear-smr': {
    heatRate: 0, ambientFactor: 0.93, degradationFactor: 1, auxiliaryFraction: 0,
    forcedOutageRate: 0.07, withdrawalGalPerMWh: 0, consumptionGalPerMWh: 0, footprintAcresPerMW: 1,
    citation: 'Project phase-feasibility defaults: 93% firm capacity credit and 1.0 acre/MW planning footprint. Reactor heat balance and cooling-water study required.',
  },
  'large-nuclear': {
    heatRate: 0, ambientFactor: 0.93, degradationFactor: 1, auxiliaryFraction: 0,
    forcedOutageRate: 0.07, withdrawalGalPerMWh: 0, consumptionGalPerMWh: 0, footprintAcresPerMW: 0.9,
    citation: 'Project phase-feasibility defaults: 93% firm capacity credit and 0.9 acre/MW planning footprint. Plant heat balance and cooling-water study required.',
  },
};

/**
 * The technology archetype's planning ambient factor — the STABLE reference
 * evidence-derived derate suggestions are computed against (never the live,
 * possibly already-derated scenario value, which would compound).
 */
export function planningAmbientFactor(technology: GenerationTechnology): number {
  return ARCHETYPES[technology].ambientFactor;
}

/**
 * Screening basis used when a modeller first enables carbon capture.
 *
 * These are deliberately explicit, low-confidence planning inputs rather than
 * implied solver constants. Project-specific FEED values must replace them
 * before a scenario is treated as design-grade.
 */
export const CCS_PLANNING_DEFAULTS: Readonly<CcsConfiguration> = Object.freeze({
  enabled: true,
  captureRate: 0.9,
  specificPowerKWhPerTonneCaptured: 250,
  waterWithdrawalGalPerTonneCaptured: 50,
  waterConsumptionGalPerTonneCaptured: 40,
  footprintAcres: 15,
  compressionFootprintAcres: 5,
  pipelineExportConfirmed: false,
});

const CCS_BASIS_FIELDS = [
  'captureRate',
  'specificPowerKWhPerTonneCaptured',
  'waterWithdrawalGalPerTonneCaptured',
  'waterConsumptionGalPerTonneCaptured',
  'footprintAcres',
  'compressionFootprintAcres',
] as const satisfies readonly (keyof CcsConfiguration)[];

/**
 * Toggle CCS without allowing an enabled scenario to retain the all-zero
 * disabled placeholder. Re-enabling a previously edited basis preserves the
 * modeller's values, including intentionally invalid values that the solver
 * must surface instead of silently replacing.
 */
export function setCcsEnabledWithPlanningDefaults(
  current: CcsConfiguration,
  enabled: boolean,
): CcsConfiguration {
  if (!enabled) return { ...current, enabled: false };

  const hasConfiguredBasis = CCS_BASIS_FIELDS.some((field) => current[field] !== 0);
  if (hasConfiguredBasis) return { ...current, enabled: true };

  return {
    ...CCS_PLANNING_DEFAULTS,
    pipelineExportConfirmed: current.pipelineExportConfirmed,
  };
}

/** Build a fully labelled planning scenario; every archetype value remains editable. */
export function buildPlanningScenario(args: PlanningScenarioArgs): GenerationImpactScenarioInput {
  const archetype = ARCHETYPES[args.technology];
  const count = Math.max(1, Math.trunc(args.blockCount));
  const units = Array.from({ length: count }, (_, index) => ({
    id: `unit-${index + 1}`,
    label: `${args.technology === 'combined-cycle-block' ? 'Block' : 'Unit'} ${index + 1}`,
    technology: args.technology,
    contingencyGroupId: `group-${index + 1}`,
    count: 1,
    isoGrossMWPerUnit: args.grossMWPerBlock,
    ambientFactor: archetype.ambientFactor,
    degradationFactor: archetype.degradationFactor,
    auxiliaryLoadMWPerUnit: args.grossMWPerBlock * archetype.auxiliaryFraction,
    heatRateBtuPerKWhHhv: archetype.heatRate,
    forcedOutageRate: archetype.forcedOutageRate,
  }));
  return {
    schemaVersion: 1,
    name: args.name ?? `${count} × ${args.grossMWPerBlock} MW ${args.technology}`,
    siteId: args.siteId,
    ...(args.targetPhaseId ? { targetPhaseId: args.targetPhaseId } : {}),
    gridConnection: args.gridConnection ?? 'islanded',
    facilityLoadMW: args.facilityLoadMW,
    criticalLoadMW: args.criticalLoadMW ?? args.facilityLoadMW,
    reserveMarginPct: 15,
    contingencyOutages: args.contingencyOutages ?? 1,
    annualOperatingHours: 8_000,
    units,
    ccs: args.ccsEnabled
      ? { ...CCS_PLANNING_DEFAULTS }
      : {
          enabled: false,
          captureRate: 0,
          specificPowerKWhPerTonneCaptured: 0,
          waterWithdrawalGalPerTonneCaptured: 0,
          waterConsumptionGalPerTonneCaptured: 0,
          footprintAcres: 0,
          compressionFootprintAcres: 0,
          pipelineExportConfirmed: false,
        },
    water: {
      generationWithdrawalGalPerMWhGross: archetype.withdrawalGalPerMWh,
      generationConsumptionGalPerMWhGross: archetype.consumptionGalPerMWh,
    },
    footprint: { plantAcresPerGrossMW: archetype.footprintAcresPerMW, coolingAcres: 10, switchyardAcres: 10 },
    receptors: [],
    lighting: {},
    assumptions: [
      { field: 'criticalLoadMW', value: args.criticalLoadMW ?? args.facilityLoadMW, source: args.criticalLoadMW == null ? 'planning-default' : 'user', label: 'Critical load basis', confidence: 'medium' },
      { field: 'gridConnection', value: args.gridConnection ?? 'islanded', source: args.gridConnection == null ? 'planning-default' : 'project-intent', label: 'Grid relationship', citation: args.gridConnection == null ? 'Platform planning default; declare the grid relationship in Project Intent.' : 'Project Intent phase power-source configuration.', confidence: args.gridConnection == null ? 'low' : 'high' },
      { field: 'annualOperatingHours', value: 8_000, source: 'planning-default', label: 'Full-load operating hours', citation: 'Platform screening default (~91% annual availability); replace with the contracted duty profile.', confidence: 'low' },
      { field: 'contingencyOutages', value: args.contingencyOutages ?? 1, source: args.contingencyOutages == null ? 'planning-default' : 'project-intent', label: 'Resilience criterion', citation: args.contingencyOutages == null ? 'Platform screening default N-1; confirm against the redundancy class in Project Intent.' : 'Derived from the redundancy class declared in Project Intent.', confidence: args.contingencyOutages == null ? 'low' : 'high' },
      { field: 'reserveMarginPct', value: 15, source: 'planning-default', label: 'Planning reserve margin', citation: 'Platform RESERVE_MARGIN_PCT; NERC resource-adequacy planning practice.', confidence: 'medium' },
      { field: 'technologyArchetype', value: args.technology, source: 'planning-default', label: 'Generation technology planning archetype', citation: archetype.citation, confidence: 'low' },
      { field: 'ccsArchetype', value: args.ccsEnabled ?? false, source: 'planning-default', label: 'Capture-through-compression planning archetype', citation: 'DOE/NETL planning basis; project-specific FEED required.', confidence: 'low' },
    ],
    ...(args.layoutAnchor ? { layoutAnchor: args.layoutAnchor } : {}),
  };
}
